/**
 * AdBlockGuard
 * ─────────────────────────────────────────────────────
 * Premium AdBlock detection utility that displays an unclosable,
 * high-fidelity glassmorphism overlay blocking access to tools
 * if an ad blocker or privacy extension is active.
 *
 * Detection Mechanisms:
 *   1. DOM Bait Check: Creates a hidden div with common ad class names ('adsbox ad-placement banner-ad').
 *      Ad blockers automatically hide or remove these elements.
 *   2. Network Fetch Probe: Attempts a no-cors fetch to the active banner ad script URL.
 *      Ad blockers intercept and reject network requests to known ad domains.
 */

import React, { useState, useEffect } from 'react';
import { ShieldAlert, RefreshCw, AlertOctagon, CheckCircle2 } from 'lucide-react';

const PROBE_URL = 'https://www.highperformanceformat.com/54b7aea6966a4019ae8c4d79d6e0a9e6/invoke.js';

export const AdBlockGuard = () => {
  const [adBlockDetected, setAdBlockDetected] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const checkAdBlocker = async () => {
    setIsChecking(true);
    let detected = false;

    // Method 1: DOM Bait Detection
    const bait = document.createElement('div');
    bait.className = 'adsbox ad-placement banner-ad sponsor-ad pub_300x250 pub_300x250m';
    bait.style.position = 'absolute';
    bait.style.top = '-999px';
    bait.style.left = '-999px';
    bait.style.height = '10px';
    bait.style.width = '10px';
    document.body.appendChild(bait);

    // Allow browser a moment to apply extension stylesheet rules
    await new Promise((res) => setTimeout(res, 150));

    if (bait.offsetHeight === 0 || bait.style.display === 'none' || window.getComputedStyle(bait).display === 'none') {
      detected = true;
    }
    document.body.removeChild(bait);

    // Method 2: Network Request Probe (if DOM check passed)
    if (!detected) {
      try {
        await fetch(PROBE_URL, { method: 'HEAD', mode: 'no-cors', cache: 'no-store' });
      } catch (err) {
        // Fetch failed due to extension blocking the network connection
        detected = true;
      }
    }

    setAdBlockDetected(detected);
    setIsChecking(false);
  };

  useEffect(() => {
    // Initial check on mount
    checkAdBlocker();

    // Periodic check to catch ad blockers toggled during session
    const interval = setInterval(checkAdBlocker, 5000);
    return () => clearInterval(interval);
  }, []);

  if (!adBlockDetected) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(15, 23, 42, 0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        zIndex: 9999999, // Sit above everything
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        animation: 'modalFadeIn 0.3s ease-out',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '28px',
          padding: '40px 36px',
          maxWidth: '520px',
          width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.55)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          border: '1px solid rgba(226, 232, 240, 0.8)',
        }}
      >
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(239, 68, 68, 0.12)',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            boxShadow: '0 0 30px rgba(239, 68, 68, 0.2)',
          }}
        >
          <ShieldAlert size={42} strokeWidth={2.2} />
        </div>

        <h2
          style={{
            fontSize: '1.75rem',
            fontWeight: 900,
            color: 'var(--text-primary)',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
          }}
        >
          Ad Blocker Detected
        </h2>

        <p
          style={{
            fontSize: '1rem',
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
            marginBottom: '28px',
          }}
        >
          Our developer tools are 100% free to use. We rely on non-intrusive display advertisements to keep our servers running and maintain premium open-source software. Please pause or disable your ad blocker to continue using the suite.
        </p>

        {/* Steps Card */}
        <div
          style={{
            width: '100%',
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '32px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 600 }}>
            <AlertOctagon size={18} style={{ color: '#f59e0b', flexShrink: 0 }} />
            <span>Click your ad blocker extension icon in the browser toolbar.</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: 600 }}>
            <CheckCircle2 size={18} style={{ color: '#10b981', flexShrink: 0 }} />
            <span>Select &quot;Pause on this site&quot; or &quot;Disable for toolmetrico.com&quot;.</span>
          </div>
        </div>

        {/* Action Buttons */}
        <button
          onClick={checkAdBlocker}
          disabled={isChecking}
          style={{
            width: '100%',
            background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '14px',
            padding: '16px 24px',
            fontSize: '1.05rem',
            fontWeight: 800,
            cursor: isChecking ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            boxShadow: '0 10px 25px rgba(5, 117, 251, 0.35)',
            transition: 'transform 0.2s, opacity 0.2s',
            opacity: isChecking ? 0.7 : 1,
          }}
        >
          <RefreshCw size={20} style={{ animation: isChecking ? 'spin 1s linear infinite' : 'none' }} />
          {isChecking ? 'Verifying Ad Status...' : 'I Have Disabled AdBlock'}
        </button>
      </div>

      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdBlockGuard;
